'use client';
import { decodeToken } from '@ahm/common-helpers';
import { useEffect, useState, useMemo } from 'react';
import { useMomoActivateUserMutation } from '../api/mutate-activate-momo';
import { useSearchParams } from '../lib/navigation';
import { useAuthStore } from '../store/auth-store-provider';
import {
  MINI_APP_LIST,
  WEBINAPP_CONFIGS,
  WEBINAPP_ONLY_PATHS,
  WEBINAPP_MOMO_ID,
  WEBINAPP_MOMO_MINIAPP,
  type WebinappId,
} from '../webinapp';

interface UseWebinappRestrictionsProps {
  pathname: string;
  isMobile: boolean;
  webinappPaths?: string[];
}

type UseWebinappRestrictionsReturn = {
  showWebinappRestriction: boolean;
  showMobileRestriction: boolean;
  isProcessWebinapp: boolean;
  error: string | null;
  isWebInApp: boolean;
  webInAppId: string | null;
};

export function useWebinappRestrictions({
  pathname,
  isMobile,
  webinappPaths = [],
}: UseWebinappRestrictionsProps): UseWebinappRestrictionsReturn {
  const { setSession } = useAuthStore((state) => state);
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const webinapp = searchParams.get('webinapp') as WebinappId | null;
  const decoded = token ? decodeToken(token) : null;

  const webInAppSearchParam = webinapp;
  const momoParams = {
    checksum: searchParams.get('checksum') ?? '',
    name: searchParams.get('name') ?? '',
    mobile: searchParams.get('mobile') ?? '',
    email: searchParams.get('email') ?? '',
  };

  const isWebInApp = Boolean(
    webInAppSearchParam &&
      [WEBINAPP_MOMO_ID, WEBINAPP_MOMO_MINIAPP, ...MINI_APP_LIST].includes(
        webInAppSearchParam
      )
  );

  const [error, setError] = useState<string | null>(null);
  const { mutate: mutateMoMoActivate, isPending: isPendingMomoActivate } =
    useMomoActivateUserMutation();
  const isMoMo =
    webinapp === WEBINAPP_MOMO_ID || webinapp === WEBINAPP_MOMO_MINIAPP;
  const hasMoMoParams =
    !!momoParams.email &&
    !!momoParams.mobile &&
    !!momoParams.name &&
    !!momoParams.checksum;

  // Memoize allowed user webinapp ids
  const allowedUserWebinapps = [
    WEBINAPP_CONFIGS.ahauser.id,
    WEBINAPP_CONFIGS.ahamove.id,
    ...MINI_APP_LIST,
  ];

  // Memoize all webinapp only paths
  const allWebinappOnlyPaths = webinappPaths.length
    ? Array.from(new Set([...WEBINAPP_ONLY_PATHS, ...webinappPaths]))
    : WEBINAPP_ONLY_PATHS;

  // Mobile restriction logic
  function shouldShowMobileRestriction() {
    if (!(isMobile && webinapp)) return false;
    if (isMoMo) return !hasMoMoParams;
    if (!token || !decoded) return true;
    if (
      decoded.typ === 'supplier' &&
      webinapp !== WEBINAPP_CONFIGS.ahadriver.id
    )
      return true;
    if (decoded.typ === 'user' && !allowedUserWebinapps.includes(webinapp))
      return true;
    return false;
  }
  const showMobileRestriction = shouldShowMobileRestriction();

  // MoMo sign-in effect
  useEffect(() => {
    if (!isWebInApp) return;
    if (
      webInAppSearchParam === WEBINAPP_MOMO_ID ||
      webInAppSearchParam === WEBINAPP_MOMO_MINIAPP
    ) {
      if (!hasMoMoParams) {
        setError('Missing MoMo params');
        return;
      }
      setError(null);
      const config = WEBINAPP_CONFIGS[webInAppSearchParam as WebinappId];
      const payload = { ...config, ...momoParams };
      mutateMoMoActivate(payload, {
        onSuccess: (response: any) => {
          if (response?.token && response?.user) {
            setSession(response);
          } else {
            setError('Invalid response data');
          }
        },
        onError: (err: any) => {
          setError(err?.message || 'Authentication failed');
        },
      });
    }
  }, [
    isWebInApp,
    webInAppSearchParam,
    momoParams.checksum,
    momoParams.name,
    momoParams.mobile,
    momoParams.email,
  ]);

  // Webinapp restriction logic
  const showWebinappRestriction =
    (!isMobile || !webinapp) && allWebinappOnlyPaths.includes(pathname);

  return useMemo(
    () => ({
      showWebinappRestriction,
      showMobileRestriction,
      isProcessWebinapp: isPendingMomoActivate,
      error,
      isWebInApp,
      webInAppId: webInAppSearchParam,
    }),
    [
      showWebinappRestriction,
      showMobileRestriction,
      isPendingMomoActivate,
      error,
      isWebInApp,
      webInAppSearchParam,
    ]
  );
}
